import React, { useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Heart, Shield, BookOpen } from 'lucide-react';
import NavBar from '@/components/NavBar';
import { BOOKING_URL } from '@/constants/urls';
import LegalServiceSchema from '@/components/LegalServiceSchema';

const MedicalDisclaimerPage: React.FC = () => {
  useEffect(() => {
    document.title = "Medical Disclaimer | Women and The Way - Chinese Medicine";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Medical disclaimer for Women and The Way Chinese Medicine clinic. Important information about our services, limitations, and AHPRA compliance.");
    }
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', "Medical Disclaimer | Women and The Way");
    }
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', "Medical disclaimer for Women and The Way Chinese Medicine clinic. Important information about our services and limitations.");
    }
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', "https://womenandtheway.com.au/medical-disclaimer");
    }
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', "Medical Disclaimer | Women and The Way");
    }
    return () => {
      document.title = "Acupuncture Sunshine Coast & Caloundra | Women and The Way - Chinese Medicine";
    };
  }, []);

  const handleBooking = () => {
    window.open(BOOKING_URL, '_blank');
  };

  return (
    <>
      <LegalServiceSchema
        pageType="medical-disclaimer"
        title="Medical Disclaimer | Women and The Way"
        description="Medical disclaimer for Women and The Way Chinese Medicine clinic. Important information about our services, limitations, and AHPRA compliance."
        url="https://womenandtheway.com.au/medical-disclaimer"
      />
      <NavBar />
      <div className="min-h-screen bg-warm-beige pt-24 md:pt-32">
        {/* Hero Section */}
        <section className="py-16 px-6 bg-gradient-to-r from-sage-50 via-white to-amber-50">
          <div className="container mx-auto max-w-4xl text-center">
            <div className="mb-8">
              <AlertTriangle className="w-12 h-12 text-sage-green mx-auto mb-6" />
              <h1 className="font-serif text-4xl md:text-5xl font-bold text-earth-brown mb-6 leading-tight">
                Medical Disclaimer
              </h1>
              <p className="text-xl text-gray-600 font-light">
                In the spirit of transparency and professional integrity
              </p>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <main className="py-12 px-6">
          <div className="container mx-auto max-w-4xl">
            {/* Introduction */}
            <Card className="bg-white border-2 border-amber-200/50 shadow-sm mb-8 rounded-2xl">
              <CardContent className="p-8">
                <h2 className="font-serif text-2xl font-bold mb-6 text-earth-brown">In the Spirit of Transparency</h2>
                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    The information shared across our website, social media, handouts, workshops, and one-on-one sessions is offered in the spirit of education, empowerment, and support  -  not as a substitute for professional medical advice, diagnosis, or treatment.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Disclaimer Sections */}
            <div className="space-y-8">
              {/* 1. Not Medical Advice */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm rounded-2xl">
                <CardContent className="p-8">
                  <h3 className="font-serif text-xl font-bold mb-4 flex items-center text-earth-brown">
                    <span className="text-2xl mr-3">⚠️</span>
                    1. Not Medical Advice
                  </h3>
                  <p className="text-gray-700 mb-4">
                    All content and communications from Women and the Way are intended for general wellbeing and informational purposes only. They are not intended to:
                  </p>
                  <ul className="space-y-2 text-gray-700 ml-4 mb-4">
                    <li className="flex items-start"><span className="text-sage-green mr-2">•</span>Diagnose any medical condition</li>
                    <li className="flex items-start"><span className="text-sage-green mr-2">•</span>Replace the advice of your doctor, specialist, or other registered healthcare provider</li>
                    <li className="flex items-start"><span className="text-sage-green mr-2">•</span>Serve as the sole basis for decisions about your health</li>
                  </ul>
                  <p className="text-gray-700">
                    Always consult your medical provider before making changes to your medication, care plan, or health regime.
                  </p>
                </CardContent>
              </Card>

              {/* 2. Traditional Medicine Framework */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm rounded-2xl">
                <CardContent className="p-8">
                  <h3 className="font-serif text-xl font-bold mb-4 flex items-center text-earth-brown">
                    <span className="text-2xl mr-3">🌿</span>
                    2. Traditional Medicine Framework
                  </h3>
                  <p className="text-gray-700">
                    Our services are grounded in the principles of Chinese medicine  -  a holistic, traditional health system. While we value its wisdom, it is distinct from biomedicine. Treatment outcomes are variable and individual.
                  </p>
                </CardContent>
              </Card>

              {/* 3. Use of Testimonials and Outcomes */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm rounded-2xl">
                <CardContent className="p-8">
                  <h3 className="font-serif text-xl font-bold mb-4 flex items-center text-earth-brown">
                    <span className="text-2xl mr-3">💬</span>
                    3. Use of Testimonials and Outcomes
                  </h3>
                  <p className="text-gray-700">
                    In accordance with AHPRA guidelines, we do not publish testimonials relating to clinical outcomes. Any case examples or experiences shared are de-identified, anecdotal, and not indicative of guaranteed results.
                  </p>
                </CardContent>
              </Card>

              {/* 4. Complementary, Not Alternative */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm rounded-2xl">
                <CardContent className="p-8">
                  <h3 className="font-serif text-xl font-bold mb-4 flex items-center text-earth-brown">
                    <span className="text-2xl mr-3">🤝</span>
                    4. Complementary, Not Alternative
                  </h3>
                  <p className="text-gray-700">
                    We strongly advocate an integrative approach to health. Our treatments are complementary, working best alongside  -  not instead of  -  evidence-based medical care.
                  </p>
                </CardContent>
              </Card>

              {/* 5. No Guarantee of Outcome */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm rounded-2xl">
                <CardContent className="p-8">
                  <h3 className="font-serif text-xl font-bold mb-4 flex items-center text-earth-brown">
                    <span className="text-2xl mr-3">💫</span>
                    5. No Guarantee of Outcome
                  </h3>
                  <p className="text-gray-700">
                    Healing is a journey. Every body responds uniquely. While we bring skill, intention, and experience, we do not promise outcomes or specific timelines. What we offer is presence, care, and professional support.
                  </p>
                </CardContent>
              </Card>

              {/* 6. Payments, Packages & Cancellations */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm rounded-2xl">
                <CardContent className="p-8">
                  <h3 className="font-serif text-xl font-bold mb-4 flex items-center text-earth-brown">
                    <span className="text-2xl mr-3">💰</span>
                    6. Payments, Packages & Cancellations
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start"><span className="text-sage-green mr-2">•</span>Full payment is due at time of booking or service</li>
                    <li className="flex items-start"><span className="text-sage-green mr-2">•</span>Packages are non-refundable but transferable unless otherwise specified</li>
                    <li className="flex items-start"><span className="text-sage-green mr-2">•</span>Cancellations require 24 hours' notice; less than this may incur a fee</li>
                    <li className="flex items-start"><span className="text-sage-green mr-2">•</span>Late arrivals may shorten your treatment time</li>
                  </ul>
                </CardContent>
              </Card>

              {/* 7. Products & Herbal Formulas */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm rounded-2xl">
                <CardContent className="p-8">
                  <h3 className="font-serif text-xl font-bold mb-4 flex items-center text-earth-brown">
                    <span className="text-2xl mr-3">🌱</span>
                    7. Products & Herbal Formulas
                  </h3>
                  <p className="text-gray-700">
                    Any recommendations for herbs or supplements will be based on your individual needs and sourced from reputable suppliers. Instructions for safe use will be provided. These are not intended to replace prescribed medications.
                  </p>
                </CardContent>
              </Card>

              {/* 8. Community Offerings */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm rounded-2xl">
                <CardContent className="p-8">
                  <h3 className="font-serif text-xl font-bold mb-4 flex items-center text-earth-brown">
                    <span className="text-2xl mr-3">👥</span>
                    8. Community Offerings
                  </h3>
                  <p className="text-gray-700">
                    Workshops and other group offerings are delivered with the same integrity and privacy expectations as individual care. Shared spaces foster connection but always honour personal boundaries.
                  </p>
                </CardContent>
              </Card>

              {/* 9. Feedback & Complaints */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm rounded-2xl">
                <CardContent className="p-8">
                  <h3 className="font-serif text-xl font-bold mb-4 flex items-center text-earth-brown">
                    <span className="text-2xl mr-3">📞</span>
                    9. Feedback & Complaints
                  </h3>
                  <p className="text-gray-700">
                    We welcome feedback and hold a culture of continuous improvement. If you have concerns, please contact us directly. If needed, complaints may also be made via the Office of the Health Ombudsman (QLD) or AHPRA.
                  </p>
                </CardContent>
              </Card>

              {/* 10. Intellectual Property */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm rounded-2xl">
                <CardContent className="p-8">
                  <h3 className="font-serif text-xl font-bold mb-4 flex items-center text-earth-brown">
                    <span className="text-2xl mr-3">📝</span>
                    10. Intellectual Property
                  </h3>
                  <p className="text-gray-700">
                    All content on our website and printed materials remains the property of Women and the Way and may not be reproduced without permission.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Closing Section */}
            <Card className="bg-sage-800 text-white border-2 border-amber-200/50 shadow-sm mt-8 rounded-2xl">
              <CardContent className="p-8">
                <h3 className="font-serif text-2xl font-bold mb-6">In Closing</h3>
                <p className="text-sage-100 leading-relaxed mb-4">
                  These Terms are not just legal  -  they are a promise. A promise to walk with integrity, to serve with heart, and to offer every woman who enters our care a space where healing, rest, and reconnection are honoured.
                </p>
                <div className="text-center mt-6">
                  <p className="text-sage-200 font-medium">With respect and warmth,</p>
                  <p className="text-white font-serif text-lg font-semibold">Women and the Way</p>
                </div>
              </CardContent>
            </Card>

            {/* Call to Action */}
            <div className="text-center mt-12">
              <Button 
                size="lg" 
                className="bg-sage-600 hover:bg-sage-700 text-white px-8 py-4 text-lg font-semibold rounded-full"
                onClick={handleBooking}
              >
                Book Your Consultation
                <Shield className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default MedicalDisclaimerPage; 